import { t } from "@lingui/macro";

import { isTrialing, type SubscriptionLike } from "@/client/utils/subscription";

import type { PricingCycle } from "./pricing-prices";

/** `trialist`: known running trial (priced line). `unknown`: signed-in lookup pending or failed. */
export type TrialTermsMode = "trialist" | "unknown";

/**
 * True for a signed-in user whose trial is running RIGHT NOW: status TRIALING
 * with a `lsCurrentPeriodEnd` still in the future. Same gate as the trial
 * banner (#653), so the two surfaces never disagree about who is a trialist.
 */
export const isActiveTrialist = (
  subscription: SubscriptionLike,
  now: Date = new Date(),
): boolean => {
  if (!subscription?.lsCurrentPeriodEnd) return false;
  const periodEnd = new Date(subscription.lsCurrentPeriodEnd).getTime();
  return isTrialing(subscription) && Number.isFinite(periodEnd) && periodEnd > now.getTime();
};

/**
 * What a trialist's checkout charges, stated under the plan's CTA. Same facts
 * and the exact wording of the trial banner and the cancelled-checkout retry:
 * the checkout charges today, and the subscription replaces the remaining free
 * trial days. The first trialist to reach /pricing started an ULTIMATE
 * checkout and left without paying (2026-10-15) — nothing on the page had said
 * the trial ends on purchase.
 */
export const trialistPurchaseTerms = (price: string, cycle: PricingCycle): string => {
  if (cycle === "weekly") {
    return t`${price}/week, charged today. Your free trial days end when you subscribe. Cancel anytime.`;
  }
  if (cycle === "monthly") {
    return t`${price}/month, charged today. Your free trial days end when you subscribe. Cancel anytime.`;
  }
  return t`${price}/year, charged today. Your free trial days end when you subscribe. Cancel anytime.`;
};

/**
 * Shown to a returning user while we cannot yet tell whether they are on a
 * trial (the /user/me lookup is pending or failed). True for every audience:
 * the checkout charges now, and a running trial, if any, ends.
 */
export const unknownAccountPurchaseTerms = (): string =>
  t`Subscribing charges you today. If you are on a free trial, your remaining trial days end. Cancel anytime.`;
